import { CommonHelperFunctions } from "@kuika/kuika-cl-model";
import Axios, { AxiosResponse } from "axios";

const aiChatApiUrl = `${CommonHelperFunctions.GetAiChatUrl()}/api`;

export interface AiChatBalanceResponse {
  creditsRemaining: number;
  version?: number;
  projectionId?: string;
  balanceRevision?: string | null;
  creditsConsumed: number;
  totalPurchasedCents: number;
  totalGrantedCredits?: number;
  totalSpentUsd: number;
  starterCreditsIncluded?: number;
}

export interface AiChatCapabilitiesResponse {
  categories: AiChatCapabilityCategory[];
}

/** One turn's chat content from the chat server's telemetry (KOA-18066). */
export interface AiChatRequestContentResponse {
  requestId: string;
  userName: string | null;
  mode: string | null;
  model: string | null;
  startedAt: number | null;
  promptText: string | null;
  responseText: string | null;
  available: boolean;
  source: "telemetry";
}

// Numeric enum on purpose. The wire payload from mcp-chat-server's
// /config endpoint carries this as an integer so the literal provider
// name (e.g. "openrouter") never appears in network responses visible
// to chat-panel users in DevTools. Designer ↔ mcp-chat-server must
// agree on these codes — mcp-chat-server/src/routes/config.ts maps
// its internal ProviderName string to the same integers.
export enum AiChatProviderName {
  Anthropic = 0,
  OpenAI = 1,
  OpenAICompatible = 2,
  OpenRouter = 3
}

export interface AiChatConfigResponse {
  provider: AiChatProviderName;
  /** Whether the server offers the project-analysis (discovery) walk. Absent on
   *  an older server, which is treated as "off" — a control that does nothing is
   *  worse than a missing one. */
  projectAnalysis?: boolean;
}

export interface AiChatCapabilityTool {
  name: string;
  description: string;
}

export interface AiChatCapabilityCategory {
  name: string;
  tools: AiChatCapabilityTool[];
}

export interface SubmitRatingRequest {
  request_id: string;
  session_id: string;
  tab_id: string;
  user_id: string;
  project_id: string;
  rating: number;
  /**
   * Optional free-form reason captured when the user marks a response "Not OK"
   * (e.g. "Wrong change"). Sent alongside the numeric rating; the server may
   * ignore it until durable persistence lands.
   */
  reason?: string;
}

export class AiChatService {
  public static async GetBalance(
    userId: string,
    userName?: string,
    refresh?: boolean
  ): Promise<AxiosResponse<AiChatBalanceResponse>> {
    return Axios.get<AiChatBalanceResponse>(`${aiChatApiUrl}/balance`, {
      params: { user_id: userId, user_name: userName, ...(refresh ? { refresh: "true" } : {}) }
    });
  }

  public static async GetCapabilities(): Promise<AxiosResponse<AiChatCapabilitiesResponse>> {
    return Axios.get<AiChatCapabilitiesResponse>(`${aiChatApiUrl}/capabilities`);
  }

  /**
   * Prompt/response content of one chat turn from the mcp-chat-server's local
   * telemetry (owner analytics drawer, KOA-18066). Content is instance-local
   * and best-effort — 404/403/unavailable resolve to null so the drawer shows
   * a friendly "content unavailable" state instead of an error toast.
   */
  public static async GetRequestContent(
    requestId: string,
    signal?: AbortSignal
  ): Promise<AiChatRequestContentResponse | null> {
    try {
      const res = await Axios.get<AiChatRequestContentResponse>(
        `${aiChatApiUrl}/usage-logs/${encodeURIComponent(requestId)}`,
        { signal }
      );
      return res.data;
    } catch (error: unknown) {
      if (Axios.isCancel(error)) throw error;
      return null;
    }
  }

  public static async GetConfig(): Promise<AxiosResponse<AiChatConfigResponse>> {
    return Axios.get<AiChatConfigResponse>(`${aiChatApiUrl}/config`);
  }

  public static async SubmitRating(body: SubmitRatingRequest): Promise<AxiosResponse> {
    return Axios.post(`${aiChatApiUrl}/rating`, body);
  }
}
